import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getFirestore, collection, getDocs, deleteDoc, doc } from 'firebase/firestore'

interface CampaignItem {
  id: string
  productName: string
  status: string
  hasLandingPage: boolean
  createdAtSeconds: number
}

export default function CampaignsPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<CampaignItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const db = getFirestore()
        const snap = await getDocs(collection(db, 'campaigns'))
        const list: CampaignItem[] = []

        snap.forEach(docSnap => {
          const data = docSnap.data() as any
          list.push({
            id: docSnap.id,
            productName: data.briefing?.productName || 'Sem nome',
            status: data.status || 'draft',
            hasLandingPage: !!data.landingPage?.slug,
            createdAtSeconds: data.createdAt?.seconds || 0,
          })
        })

        list.sort((a, b) => b.createdAtSeconds - a.createdAtSeconds)
        setItems(list)
      } catch (err: any) {
        setError('Erro ao carregar campanhas: ' + err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  function statusLabel(status: string) {
    if (status === 'draft') return 'Rascunho'
    return status
  }

  async function handleDelete(id: string, productName: string, e: React.MouseEvent) {
    e.stopPropagation()
    const confirmed = window.confirm(
      `Excluir a campanha "${productName}"? Essa ação não pode ser desfeita. ` +
      `(A landing page já publicada continua acessível pelo link, mas deixa de aparecer aqui.)`
    )
    if (!confirmed) return

    setDeletingId(id)
    try {
      const db = getFirestore()
      await deleteDoc(doc(db, 'campaigns', id))
      setItems(prev => prev.filter(item => item.id !== id))
    } catch (err: any) {
      alert('Erro ao excluir campanha: ' + err.message)
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Carregando campanhas...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Campanhas</h1>
        <button
          onClick={() => navigate('/campanhas/nova')}
          className="px-5 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ background: 'linear-gradient(135deg,#6366f1,#10b981)' }}
        >
          + Nova Campanha
        </button>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-gray-400 mb-2">Nenhuma campanha criada ainda.</p>
          <p className="text-gray-500 text-sm">Clique em "Nova Campanha" para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map(item => (
            <div
              key={item.id}
              onClick={() => navigate(`/campanhas/${item.id}`)}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4 cursor-pointer hover:border-white/30 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-white font-semibold truncate">{item.productName}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">
                    {statusLabel(item.status)}
                  </span>
                  {item.hasLandingPage && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">
                      Landing page criada
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <button
                  onClick={(e) => handleDelete(item.id, item.productName, e)}
                  disabled={deletingId === item.id}
                  className="text-red-400 text-sm hover:text-red-300 transition-colors disabled:opacity-50"
                >
                  {deletingId === item.id ? 'Excluindo...' : 'Excluir'}
                </button>
                <span className="text-gray-500 text-sm">Ver →</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
